import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe, type SafeString } from '@ember/template';
import Component from '@glimmer/component';
import {
  PrismBadge,
  PrismButton,
  PrismDrawer,
  PrismIcon,
  PrismLoadingBar,
  PrismMessage,
  PrismTabsContainer,
  PrismViewSwitcher,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { and, eq, not } from 'ember-truth-helpers';
import GeneralSmartInsightsCalendarCalendarSmartInsights from 'lumen/components/general/smart-insights/calendar/calendar-smart-insights';
import GeneralSmartInsightsDetailViewListArchivedInsights from 'lumen/components/general/smart-insights/detail-view/list-archived-insights';
import GeneralSmartInsightsDetailViewSmartInsightDetailView from 'lumen/components/general/smart-insights/detail-view/smart-insight-detail-view';
import GeneralSmartInsightsDropdownPriorityFilter from 'lumen/components/general/smart-insights/dropdown-priority-filter';
import GeneralSmartInsightsListListSmartInsights from 'lumen/components/general/smart-insights/list/list-smart-insights';
import GeneralSmartInsightsUpsellAccordionYearView from 'lumen/components/general/smart-insights/upsell/accordion-year-view';
import GeneralSmartInsightsUpsellBadgeYearView from 'lumen/components/general/smart-insights/upsell/badge-year-view';
import isDrawerExpanded from 'lumen/helpers/is-drawer-expanded';
import type { SmartInsightDay } from 'lumen/models';
import type { SmartInsightPriority } from 'lumen/models/smart-insights/smart-insight-day';
import type { DrawerID } from 'lumen/services/drawer';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  DRAWER_IDS,
  HEADER_HEIGHT,
  SECOND_HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';
import {
  isSmartInsightsTab,
  isSmartInsightsView,
  SMART_INSIGHTS_TABS,
  SMART_INSIGHTS_VIEWS,
  type SmartInsightsArchiveReason,
  type SmartInsightsKpiComparisonPeriod,
  type SmartInsightsTab,
  type SmartInsightsView,
} from 'lumen/utils/constants/smart-insights';

import styles from './smart-insights-drawer.module.css';

interface GeneralSmartInsightsSmartInsightsDrawerSignature {
  Args: {
    selectedPriorities: SmartInsightPriority[];
    selectedSmartInsight: SmartInsightDay | null;
    setSelectedPriorities: (priorities: SmartInsightPriority[]) => void;
    setSmartInsightsDay: (day: string | null) => void;
    setSmartInsightsKpiComparisonPeriod: (
      period: SmartInsightsKpiComparisonPeriod,
    ) => void;
    setSmartInsightsTab: (tab: SmartInsightsTab) => void;
    setSmartInsightsView: (view: SmartInsightsView) => void;
    smartInsightsDay: string | null | undefined;
    smartInsightsKpiComparisonPeriod: SmartInsightsKpiComparisonPeriod;
    smartInsightsTab: SmartInsightsTab;
    smartInsightsView: SmartInsightsView;
    toggleSmartInsightsDrawer: () => void;
  };
  Element: HTMLButtonElement;
}

export default class GeneralSmartInsightsSmartInsightsDrawer extends Component<GeneralSmartInsightsSmartInsightsDrawerSignature> {
  @service declare access: Services['access'];
  @service declare featureToggle: Services['feature-toggle'];
  @service declare smartInsights: Services['smart-insights'];
  @service('smart-insights/upsell')
  declare smartInsightsUpsell: Services['smart-insights/upsell'];
  @service declare usageAnalytics: Services['usage-analytics'];

  SMART_INSIGHTS_DRAWER_ID: DrawerID = DRAWER_IDS.SMART_INSIGHTS_DRAWER;
  SMART_INSIGHTS_TABS = SMART_INSIGHTS_TABS;

  get currentInsightIndex(): number {
    const { selectedSmartInsight } = this.args;

    if (!selectedSmartInsight) {
      return -1;
    }

    return this.sortedFilteredInsights.findIndex(
      (insight) => insight.stayDate === selectedSmartInsight.stayDate,
    );
  }

  get drawerContentHeight(): SafeString {
    return htmlSafe(`height: calc(100vh - ${this.drawerPositionTop}px);`);
  }

  get drawerPositionTop(): number {
    return HEADER_HEIGHT - SECOND_HEADER_HEIGHT;
  }

  get filteredInsights(): SmartInsightDay[] {
    return this.insights.filter(
      (insight) =>
        this.args.selectedPriorities.includes(insight.stayDatePriority) &&
        insight.hasRenderableInsightEntries,
    );
  }

  get hasNextInsight(): boolean {
    return !!this.nextInsight;
  }

  get hasPreviousInsight(): boolean {
    return !!this.previousInsight;
  }

  get insights(): SmartInsightDay[] {
    if (
      !this.smartInsights.getSmartInsightsTask.last ||
      this.smartInsights.getSmartInsightsTask.last.isError
    ) {
      return [];
    }

    return this.smartInsights.getSmartInsightsTask.last.value ?? [];
  }

  get insightsSortedByDate(): SmartInsightDay[] {
    return this.insights.slice().sort((a, b) => {
      if (a.stayDate === b.stayDate) {
        return b.stayDateRelevance - a.stayDateRelevance;
      }

      return new Date(a.stayDate).getTime() - new Date(b.stayDate).getTime();
    });
  }

  get insightsSortedByPriority(): SmartInsightDay[] {
    return this.insights.slice().sort((a, b) => {
      if (a.stayDateRelevance === b.stayDateRelevance) {
        return new Date(a.stayDate).getTime() - new Date(b.stayDate).getTime();
      }

      return b.stayDateRelevance - a.stayDateRelevance;
    });
  }

  get nextInsight(): SmartInsightDay | undefined {
    const { smartInsightsDay } = this.args;

    if (!smartInsightsDay) {
      return undefined;
    }

    if (this.currentInsightIndex >= 0) {
      return this.sortedFilteredInsights[this.currentInsightIndex + 1];
    }

    // Selected day has no entries or no record; find the first day after it with entries
    return this.sortedFilteredInsights.find(
      (insight) => insight.stayDate > smartInsightsDay,
    );
  }

  get nextInsightInfo(): { date: string; onReview: () => void } | undefined {
    const stayDate = this.nextInsight?.stayDate;

    if (!stayDate) {
      return undefined;
    }

    return {
      date: dayjs(stayDate).format('ddd, MMM D'),
      onReview: this.onClickNext.bind(this),
    };
  }

  get previousInsight(): SmartInsightDay | undefined {
    const { smartInsightsDay } = this.args;

    if (!smartInsightsDay) {
      return undefined;
    }

    if (this.currentInsightIndex === 0) {
      return undefined;
    }

    if (this.currentInsightIndex > 0) {
      return this.sortedFilteredInsights[this.currentInsightIndex - 1];
    }

    // Selected day has no entries or no record; find the last day before it with entries
    const allPrevious = this.sortedFilteredInsights.filter(
      (insight) => insight.stayDate < smartInsightsDay,
    );
    return allPrevious.at(-1);
  }

  /** Show the empty state when a day was requested but no insight exists for it. */
  get showEmptyState(): boolean {
    return !!(
      this.args.smartInsightsDay &&
      !this.args.selectedSmartInsight &&
      !this.smartInsights.getSmartInsightsTask.isRunning
    );
  }

  /** Show the priority filter whenever no specific insight or day is selected. */
  get showPriorityFilter(): boolean {
    return !this.args.selectedSmartInsight && !this.args.smartInsightsDay;
  }

  get showUpsell(): boolean {
    return (
      this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.SMART_INSIGHTS_365_UPSELL,
      ) &&
      !this.access.hasSmartInsightsYearView &&
      this.access.hasSmartInsightsUpsell
    );
  }

  get sortedFilteredInsights(): SmartInsightDay[] {
    return this.sortedInsights.filter(
      (insight) =>
        this.args.selectedPriorities.includes(insight.stayDatePriority) &&
        insight.hasRenderableInsightEntries,
    );
  }

  get sortedInsights(): SmartInsightDay[] {
    return this.args.smartInsightsView === SMART_INSIGHTS_VIEWS.LIST
      ? this.insightsSortedByPriority
      : this.insightsSortedByDate;
  }

  get stayDateFormatted(): string {
    const { smartInsightsDay } = this.args;

    if (!smartInsightsDay) {
      return '';
    }

    return dayjs(smartInsightsDay).format('ddd, MMM D');
  }

  @action collapseDrawer(): void {
    this.args.toggleSmartInsightsDrawer();
  }

  @action async onArchiveInsight(
    reason: SmartInsightsArchiveReason,
  ): Promise<void> {
    const { smartInsightsDay } = this.args;

    if (!smartInsightsDay) {
      return;
    }

    try {
      await this.smartInsights.archiveInsightTask.perform(
        smartInsightsDay,
        reason,
        undefined,
        {
          onNavigate: (stayDate: string): void => {
            this.args.setSmartInsightsTab(SMART_INSIGHTS_TABS.ACTIVE);
            this.args.setSmartInsightsDay(stayDate);
          },
          product: 'smart-insights',
        },
      );

      this.usageAnalytics.track('Smart Insights Insight Archived', {
        eventTrigger: 'smart-insights-archive-dropdown',
        product: 'smart-insights',
        actioned_status: reason,
        ...this.smartInsights.getSmartInsightUsageAnalyticsContext(
          this.args.selectedSmartInsight,
          smartInsightsDay,
        ),
      });

      // After successful archive, go back to calendar/list view
      this.args.setSmartInsightsTab(SMART_INSIGHTS_TABS.ACTIVE);
      this.args.setSmartInsightsDay(null);
    } catch {
      // Error is already handled in the service
    }
  }

  @action onClickBack(): void {
    this.args.setSmartInsightsTab(SMART_INSIGHTS_TABS.ACTIVE);
    this.args.setSmartInsightsDay(null);
  }

  @action onClickNext(): void {
    const next = this.nextInsight;

    if (next) {
      this.args.setSmartInsightsDay(next.stayDate);
      this.smartInsights.fetchArchived(next.stayDate);
    }
  }

  @action onClickPrevious(): void {
    const previous = this.previousInsight;

    if (previous) {
      this.args.setSmartInsightsDay(previous.stayDate);
      this.smartInsights.fetchArchived(previous.stayDate);
    }
  }

  @action onSelectTab(tabId: string): void {
    if (!isSmartInsightsTab(tabId)) {
      return;
    }

    this.args.setSmartInsightsTab(tabId);

    this.usageAnalytics.track('Smart Insights Tab Selected', {
      eventTrigger: 'smart-insights-tabs',
      product: 'smart-insights',
      tab: tabId,
    });
  }

  @action onSetActiveButtonId(buttonId: string): void {
    if (!isSmartInsightsView(buttonId)) {
      return;
    }

    if (this.args.smartInsightsView === buttonId) {
      return;
    }

    this.args.setSmartInsightsView(buttonId);

    this.usageAnalytics.track('Smart Insights Layout Toggled', {
      eventTrigger: 'smart-insights-view-switcher',
      product: 'smart-insights',
      view: buttonId,
    });
  }

  @action selectDate(day: string | null): void {
    this.args.setSmartInsightsDay(day);
    if (day) {
      this.smartInsights.fetchArchived(day);
    }
  }

  @action selectSmartInsightsDay(smartInsightsDay: SmartInsightDay): void {
    this.args.setSmartInsightsDay(smartInsightsDay.stayDate);
    this.smartInsights.fetchArchived(smartInsightsDay.stayDate);
  }

  <template>
    <PrismDrawer
      @contentPadding="none"
      @isOpen={{isDrawerExpanded this.SMART_INSIGHTS_DRAWER_ID}}
      @positionTop={{this.drawerPositionTop}}
      class={{styles.smart-insights-drawer}}
      data-test-id="smart-insights-drawer"
    >
      <:content>
        <div
          class="smart-insights-drawer__content relative flex flex-col"
          style={{this.drawerContentHeight}}
        >
          {{#if this.smartInsights.getSmartInsightsTask.isRunning}}
            <PrismLoadingBar />
          {{/if}}

          <div class={{styles.smart-insights-drawer__header}}>
            <div class="flex w-full flex-row justify-between">
              <div class="grid grid-cols-[auto_1fr] gap-x-200">
                <div class="flex items-center gap-200 self-center">
                  {{#if this.showUpsell}}
                    <GeneralSmartInsightsUpsellBadgeYearView
                      @upsellPath={{this.smartInsightsUpsell.upsellPath}}
                    />
                  {{/if}}
                  {{#if @smartInsightsDay}}
                    {{! Not using a button to avoid the Smart Insights title from jumping when switching between the calendar and detail view }}
                    <PrismIcon
                      @name="arrow-left"
                      class="cursor-pointer"
                      data-test-id="back-button"
                      role="button"
                      {{on "click" this.onClickBack}}
                    />
                  {{else}}
                    <PrismIcon
                      @color="on-intelligence"
                      @name="smart-insights"
                    />
                  {{/if}}
                </div>
                <h3>{{t "smart_insights.title"}}</h3>
                <p class="text-200--regular text-neutral-subdued col-start-2">
                  {{t "smart_insights.powered_by"}}
                </p>
              </div>

              <div class="action">
                <PrismButton
                  @buttonType="ghost"
                  @icon="cross"
                  @isSmall={{true}}
                  @onClick={{this.collapseDrawer}}
                  data-test-id="drawer-close-button"
                />
              </div>
            </div>

            <div
              class="flex min-h-[36px] w-full flex-row items-center justify-between"
            >
              {{#if this.showPriorityFilter}}
                <div
                  class="flex items-center gap-100"
                  data-test-id="dropdown-priority-filter"
                >
                  <GeneralSmartInsightsDropdownPriorityFilter
                    @isSmall={{true}}
                    @selectedPriorities={{@selectedPriorities}}
                    @setSelectedPriorities={{@setSelectedPriorities}}
                  />
                </div>
              {{else if @smartInsightsDay}}
                <div class="flex items-center gap-200">
                  <div class="flex gap-100">
                    <PrismButton
                      @buttonType="secondary"
                      @icon="chevron-left"
                      @isDisabled={{not this.hasPreviousInsight}}
                      @isSmall={{true}}
                      @onClick={{this.onClickPrevious}}
                      data-test-id="navigation-previous-button"
                    />
                    <PrismButton
                      @buttonType="secondary"
                      @icon="chevron-right"
                      @isDisabled={{not this.hasNextInsight}}
                      @isSmall={{true}}
                      @onClick={{this.onClickNext}}
                      data-test-id="navigation-next-button"
                    />
                  </div>
                  <div
                    class="text-300--medium text-neutral-subdued"
                    data-test-id="detail-view-date"
                  >
                    {{this.stayDateFormatted}}
                  </div>
                </div>
              {{/if}}

              {{#unless @smartInsightsDay}}
                <div data-test-id="view-switcher">
                  <PrismViewSwitcher
                    @activeButtonId={{@smartInsightsView}}
                    @onSetActiveButtonId={{this.onSetActiveButtonId}}
                    as |ViewSwitcher|
                  >
                    <ViewSwitcher.button
                      @icon="view-list"
                      @id={{SMART_INSIGHTS_VIEWS.LIST}}
                      @label=""
                      data-test-id="view-switcher-list"
                    />

                    <ViewSwitcher.button
                      @icon="view-calendar"
                      @id={{SMART_INSIGHTS_VIEWS.CALENDAR}}
                      @label=""
                      data-test-id="view-switcher-calendar"
                    />
                  </PrismViewSwitcher>
                </div>
              {{/unless}}
            </div>
          </div>

          <div class={{styles.body}}>
            {{#if this.smartInsights.hasInsightsError}}
              <div
                class="flex h-full items-center justify-center p-400"
                data-test-id="smart-insights-error-state"
              >
                <PrismMessage
                  @description={{t "general.error"}}
                  @illustration="100x100_critical"
                  @title={{t "general.error_title"}}
                />
              </div>
            {{else if @smartInsightsDay}}
              {{! template-lint-disable no-duplicate-id }}
              <PrismTabsContainer
                @activeTabId={{@smartInsightsTab}}
                @onSelectTab={{this.onSelectTab}}
                @visibleTabIds={{array
                  this.SMART_INSIGHTS_TABS.ACTIVE
                  this.SMART_INSIGHTS_TABS.ARCHIVED
                }}
                as |TabsContainer|
              >
                <TabsContainer.header>
                  <:default as |Header|>
                    <Header.tab
                      @id={{this.SMART_INSIGHTS_TABS.ACTIVE}}
                      data-test-tab="active"
                    >
                      {{t "smart_insights.tabs.active"}}
                    </Header.tab>

                    <Header.tab
                      @id={{this.SMART_INSIGHTS_TABS.ARCHIVED}}
                      data-test-tab="archived"
                    >
                      <span class="flex items-center gap-100">
                        {{t "smart_insights.tabs.archived"}}
                        {{#if
                          (and
                            this.smartInsights.archivedInsightDays.length
                            (not
                              this.smartInsights.getArchivedInsightsTask.isRunning
                            )
                            (not this.smartInsights.hasArchivedInsightsError)
                          )
                        }}
                          <PrismBadge
                            @isSmall={{true}}
                            @label={{this.smartInsights.archivedInsightDays.length}}
                            @type="neutral"
                          />
                        {{/if}}
                      </span>
                    </Header.tab>
                  </:default>
                </TabsContainer.header>

                <TabsContainer.content as |Content|>
                  <Content.tab @id={{this.SMART_INSIGHTS_TABS.ACTIVE}}>
                    {{#if this.showEmptyState}}
                      <div
                        class="rounded-200 bg-elevation-surface m-400 p-500 shadow-neutral-100"
                        data-test-id="empty-state"
                      >
                        <p class="text-300--regular">
                          {{t
                            "smart_insights.empty_state.description"
                            classNameBold="font-bold"
                            htmlSafe=true
                          }}
                        </p>
                        {{#if this.nextInsightInfo}}
                          <div class="divider-line mt-500 mb-300" />
                          <div
                            class="flex items-center justify-between gap-200"
                          >
                            <div class="flex items-center gap-200">
                              <PrismIcon
                                @color="on-intelligence"
                                @name="smart-insights"
                              />
                              <span class="text-300--regular">
                                {{t
                                  "smart_insights.empty_state.next_insight"
                                  classNameBold="font-bold"
                                  date=this.nextInsightInfo.date
                                  htmlSafe=true
                                }}
                              </span>
                            </div>
                            <PrismButton
                              @buttonType="primary"
                              @isSmall={{true}}
                              @label={{t "smart_insights.empty_state.review"}}
                              @onClick={{this.nextInsightInfo.onReview}}
                              data-test-id="review-next-insight-button"
                            />
                          </div>
                        {{/if}}
                      </div>
                    {{else if @selectedSmartInsight}}
                      <GeneralSmartInsightsDetailViewSmartInsightDetailView
                        @isArchiving={{this.smartInsights.archiveInsightTask.isRunning}}
                        @nextInsight={{this.nextInsightInfo}}
                        @onArchive={{this.onArchiveInsight}}
                        @setSmartInsightsKpiComparisonPeriod={{@setSmartInsightsKpiComparisonPeriod}}
                        @smartInsight={{@selectedSmartInsight}}
                        @smartInsightsKpiComparisonPeriod={{@smartInsightsKpiComparisonPeriod}}
                      />
                    {{/if}}
                  </Content.tab>

                  <Content.tab @id={{this.SMART_INSIGHTS_TABS.ARCHIVED}}>
                    <GeneralSmartInsightsDetailViewListArchivedInsights
                      @archivedInsights={{this.smartInsights.archivedInsightDays}}
                      @hasError={{this.smartInsights.hasArchivedInsightsError}}
                      @isLoading={{this.smartInsights.getArchivedInsightsTask.isRunning}}
                    />
                  </Content.tab>
                </TabsContainer.content>
              </PrismTabsContainer>
            {{else}}
              {{#if (eq @smartInsightsView SMART_INSIGHTS_VIEWS.LIST)}}
                <GeneralSmartInsightsListListSmartInsights
                  @insightsSorted={{this.sortedFilteredInsights}}
                  @selectSmartInsightsDay={{this.selectSmartInsightsDay}}
                >
                  <:afterList>
                    {{#if this.showUpsell}}
                      <GeneralSmartInsightsUpsellAccordionYearView />
                    {{/if}}
                  </:afterList>
                </GeneralSmartInsightsListListSmartInsights>
              {{else}}
                <GeneralSmartInsightsCalendarCalendarSmartInsights
                  @insights={{this.filteredInsights}}
                  @selectSmartInsightsDay={{this.selectDate}}
                >
                  <:afterCalendar>
                    {{#if (and this.showUpsell this.filteredInsights.length)}}
                      <GeneralSmartInsightsUpsellAccordionYearView />
                    {{/if}}
                  </:afterCalendar>
                </GeneralSmartInsightsCalendarCalendarSmartInsights>
              {{/if}}
            {{/if}}
          </div>
        </div>
      </:content>
    </PrismDrawer>
  </template>
}
